import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Truck, Boxes, Wrench, Plus, Pencil, RefreshCw, AlertTriangle, Archive, RotateCcw, X, ChevronUp, ChevronDown, ChevronsUpDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import VehicleProfile from './VehicleProfile';

const NAVY = '#0E2A47';
const GREEN = '#8FC93A';

// Asset categories. Vehicles is the first functional category; the rest are
// explicitly future categories governed by this same master-data system.
const CATEGORIES = [
  { key: 'vehicles', label: 'Vehicles', icon: Truck, status: 'active' },
  { key: 'large_equipment', label: 'Large Equipment', icon: Boxes, status: 'future' },
  { key: 'small_equipment', label: 'Small Equipment', icon: Wrench, status: 'future' },
];

const EMPTY = { veh_number: '', make: '', model: '', year: '', vin: '', tag: '' };

function VehicleFormDialog({ open, initial, onClose, onSaved, token }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(initial ? {
      veh_number: initial.veh_number || '', make: initial.make || '', model: initial.model || '',
      year: initial.year != null ? String(initial.year) : '', vin: initial.vin || '', tag: initial.tag || '',
    } : EMPTY);
  }, [open, initial]);
  if (!open) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.veh_number || !f.make || !f.model || !f.year || !f.tag) { toast.error('Vehicle #, Make, Model, Year and Tag are required.'); return; }
    setSaving(true);
    try {
      const payload = { ...f, year: parseInt(f.year, 10) };
      if (initial) await axios.put(`${API}/vehicles/${initial.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      else await axios.post(`${API}/vehicles`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(initial ? 'Vehicle updated' : 'Vehicle added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#0E2A47] focus:outline-none';
  const lbl = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="asset-vehicle-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold" style={{ color: NAVY }}>{initial ? `Edit Vehicle #${initial.veh_number}` : 'Add Vehicle'}</h3>
          <button onClick={onClose} data-testid="asset-vehicle-dialog-close" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Vehicle # *</label><input className={inputCls} data-testid="asset-vehicle-veh_number" value={f.veh_number} onChange={(e) => set('veh_number', e.target.value)} placeholder="e.g., 09" /></div>
          <div><label className={lbl}>Tag *</label><input className={inputCls} data-testid="asset-vehicle-tag" value={f.tag} onChange={(e) => set('tag', e.target.value)} placeholder="License plate / internal tag" /></div>
          <div><label className={lbl}>Make *</label><input className={inputCls} data-testid="asset-vehicle-make" value={f.make} onChange={(e) => set('make', e.target.value)} placeholder="FORD, CHEVY…" /></div>
          <div><label className={lbl}>Model *</label><input className={inputCls} data-testid="asset-vehicle-model" value={f.model} onChange={(e) => set('model', e.target.value)} placeholder="F150…" /></div>
          <div><label className={lbl}>Year *</label><input type="number" className={inputCls} data-testid="asset-vehicle-year" value={f.year} onChange={(e) => set('year', e.target.value)} placeholder="2024" /></div>
          <div><label className={lbl}>VIN</label><input className={inputCls} data-testid="asset-vehicle-vin" value={f.vin} maxLength={17} onChange={(e) => set('vin', e.target.value)} placeholder="17-digit VIN (optional)" /></div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="asset-vehicle-save" className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: NAVY }}>{saving ? 'Saving…' : (initial ? 'Update Vehicle' : 'Add Vehicle')}</button>
        </div>
      </div>
    </div>
  );
}

function StatusDialog({ open, vehicle, onClose, onSaved, token }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open || !vehicle) return null;
  const target = (vehicle.status || 'active') === 'active' ? 'retired' : 'active';
  const submit = async () => {
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/vehicles/${vehicle.id}/status`, { status: target, reason: reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(target === 'retired' ? 'Vehicle retired' : 'Vehicle reactivated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Status change failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="asset-status-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold" style={{ color: NAVY }}>{target === 'retired' ? 'Retire' : 'Reactivate'} Vehicle #{vehicle.veh_number}</h3>
        <p className="mt-1 text-sm text-slate-500">The record, VIN, tag and history are retained permanently — this is an audited status change, not a deletion.</p>
        <label className="mt-4 mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Reason *</label>
        <textarea data-testid="asset-status-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#0E2A47] focus:outline-none" placeholder={`Why is this vehicle being ${target === 'retired' ? 'retired' : 'reactivated'}?`} />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="asset-status-confirm" className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${target === 'retired' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#0E2A47]'}`}>{saving ? 'Saving…' : (target === 'retired' ? 'Retire Vehicle' : 'Reactivate Vehicle')}</button>
        </div>
      </div>
    </div>
  );
}

const SUMMARY_COLS = [
  { key: 'veh_number', label: 'Vehicle #', type: 'veh' },
  { key: 'make_model', label: 'Make & Model', type: 'text', get: (v) => `${v.make || ''} ${v.model || ''}`.trim() },
  { key: 'assigned_driver_name', label: 'Assigned Driver', type: 'text' },
  { key: 'tag', label: 'Tag Number', type: 'text' },
  { key: 'registration_expiration', label: 'Tag Expiration', type: 'date' },
  { key: 'ops_latest_service_date', label: 'Last Service', type: 'date' },
  { key: 'ops_latest_inspection_date', label: 'Last Inspection', type: 'date' },
];
const colGet = (v, col) => (col.get ? col.get(v) : v[col.key]);
const isBlank = (x) => x === null || x === undefined || String(x).trim() === '';

const VehiclesMaster = ({ canManage }) => {
  const { token, user } = useContext(AuthContext);
  const SORT_LS_KEY = `tfs_vehicle_sort_v1_${user?.id || 'anon'}`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [sortKey, setSortKey] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SORT_LS_KEY))?.key || 'veh_number'; } catch { return 'veh_number'; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SORT_LS_KEY))?.dir || 'asc'; } catch { return 'asc'; }
  });
  const [profileRefresh, setProfileRefresh] = useState(0);
  const [exporting, setExporting] = useState('');
  useEffect(() => {
    try { localStorage.setItem(SORT_LS_KEY, JSON.stringify({ key: sortKey, dir: sortDir })); } catch { /* ignore */ }
  }, [sortKey, sortDir, SORT_LS_KEY]);

  const exportView = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await axios.get(`${API}/vehicles/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { format: fmt, sort_key: sortKey, sort_dir: sortDir, search: search || undefined },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `tfs_vehicle_summary.${fmt === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(''); }
  };

  const load = () => {
    setLoading(true); setLoadError('');
    axios.get(`${API}/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setRows([]); setLoadError(`Unable to load vehicles (server error ${e.response?.status || 'network'}). This is a system error, not an empty list — please retry.`); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);
  useEffect(() => {
    axios.get(`${API}/vehicles/meta`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/personnel?active_only=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDrivers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [token]);

  const filtered = rows.filter((v) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${v.veh_number || ''} ${v.make || ''} ${v.model || ''} ${v.tag || ''} ${v.vin || ''}`.toLowerCase().includes(q);
  });
  const activeCount = rows.filter((v) => (v.status || 'active') === 'active').length;
  const retiredCount = rows.length - activeCount;

  const activeCol = SUMMARY_COLS.find((c) => c.key === sortKey) || SUMMARY_COLS[0];
  const sorted = [...filtered].sort((a, b) => {
    const av = colGet(a, activeCol), bv = colGet(b, activeCol);
    const ab = isBlank(av), bb = isBlank(bv);
    if (ab && bb) return 0;
    if (ab) return 1;   // blank / unavailable values always sort to the bottom
    if (bb) return -1;
    let cmp;
    if (activeCol.type === 'date') cmp = new Date(av).getTime() - new Date(bv).getTime();
    else if (activeCol.type === 'veh') {
      const an = Number(av), bn = Number(bv);
      cmp = (!isNaN(an) && !isNaN(bn)) ? an - bn : String(av).localeCompare(String(bv), undefined, { numeric: true });
    } else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (col.key !== sortKey) return <ChevronsUpDown size={13} className="ml-1 inline text-slate-300" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="ml-1 inline text-[#0E2A47]" />
      : <ChevronDown size={13} className="ml-1 inline text-[#0E2A47]" />;
  };
  const fmtD = (d) => {
    if (!d) return null;
    const s = String(d);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}/${m[3]}/${m[1].slice(-2)}`;
    const dt = new Date(s);
    if (isNaN(dt.getTime())) return null;
    return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
  };
  const expiryInfo = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(String(dateStr).slice(0, 10));
    if (isNaN(d.getTime())) return null;
    const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
    if (days < 0) return { label: 'Expired', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500', title: `Registration expired ${Math.abs(days)}d ago` };
    if (days <= 30) return { label: `${days}d left`, cls: 'bg-red-50 text-red-600', dot: 'bg-red-500', title: `Registration expires in ${days} days` };
    if (days <= 60) return { label: `${days}d left`, cls: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', title: `Registration expires in ${days} days` };
    return null;
  };

  return (
    <div data-testid="assets-vehicles">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold" style={{ color: NAVY }}>Vehicles</h2>
          <p className="text-sm text-slate-500">TFS-owned vehicle master records. Admin Hub owns identity &amp; master details; the Operations Hub will own inspections, maintenance &amp; operational status, linked to these masters.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={load} data-testid="assets-vehicles-refresh" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"><RefreshCw size={15} /> Refresh</button>
          <button onClick={() => exportView('xlsx')} disabled={!!exporting} data-testid="assets-export-xlsx" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"><Download size={15} /> {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}</button>
          <button onClick={() => exportView('pdf')} disabled={!!exporting} data-testid="assets-export-pdf" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"><Download size={15} /> {exporting === 'pdf' ? 'Exporting…' : 'PDF'}</button>
          {canManage && <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="assets-vehicles-add" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}><Plus size={15} /> Add Vehicle</button>}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5"><span className="text-xl font-extrabold" style={{ color: NAVY }}>{rows.length}</span><span className="ml-2 text-xs text-slate-500">Total</span></div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5"><span className="text-xl font-extrabold text-green-600">{activeCount}</span><span className="ml-2 text-xs text-slate-500">Active</span></div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5"><span className="text-xl font-extrabold text-amber-600">{retiredCount}</span><span className="ml-2 text-xs text-slate-500">Retired</span></div>
      </div>

      <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="assets-vehicles-search" placeholder="Search by vehicle #, make, model, tag or VIN…" className="mb-4 w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#0E2A47] focus:outline-none" />

      {loadError && (
        <div data-testid="assets-vehicles-error" className="mb-4 flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{loadError}</span>
          <button onClick={load} className="rounded-md border border-red-300 px-3 py-1 font-semibold">Retry</button>
        </div>
      )}

      {/* Mobile sort control */}
      <div className="mb-3 flex items-center gap-2 sm:hidden">
        <span className="text-xs font-semibold text-slate-500">Sort:</span>
        <select value={sortKey} onChange={(e) => { setSortKey(e.target.value); setSortDir('asc'); }} data-testid="assets-mobile-sort" className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          {SUMMARY_COLS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} data-testid="assets-mobile-sort-dir" className="rounded-lg border border-slate-300 p-2 text-slate-600">
          {sortDir === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Desktop: sortable table */}
      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white sm:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {SUMMARY_COLS.map((c) => (
                <th key={c.key} className="px-4 py-2.5">
                  <button onClick={() => toggleSort(c.key)} data-testid={`assets-sort-${c.key}`} className={`inline-flex items-center font-semibold uppercase tracking-wide hover:text-[#0E2A47] ${c.key === sortKey ? 'text-[#0E2A47]' : ''}`}>
                    {c.label}<SortIcon col={c} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && sorted.map((v) => {
              const active = (v.status || 'active') === 'active';
              return (
                <tr key={v.id} data-testid={`assets-vehicle-row-${v.id}`} onClick={() => setProfileId(v.id)} className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${active ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-2.5 font-semibold" style={{ color: NAVY }}>
                    {v.veh_number}{!active && <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">Retired</span>}
                  </td>
                  <td className="px-4 py-2.5">{v.make} {v.model}{v.trim ? ` ${v.trim}` : ''}</td>
                  <td className="px-4 py-2.5">{v.assigned_driver_name || <span className="text-slate-400">Pool</span>}</td>
                  <td className="px-4 py-2.5">{v.tag}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {v.registration_expiration ? (
                      <span className="inline-flex items-center gap-1.5">
                        {fmtD(v.registration_expiration)}
                        {expiryInfo(v.registration_expiration) && (
                          <span data-testid={`assets-expiry-badge-${v.id}`} title={expiryInfo(v.registration_expiration).title} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${expiryInfo(v.registration_expiration).cls}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${expiryInfo(v.registration_expiration).dot}`} />{expiryInfo(v.registration_expiration).label}
                          </span>
                        )}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{fmtD(v.ops_latest_service_date) || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-xs">{fmtD(v.ops_latest_inspection_date) || <span className="text-slate-400">—</span>}</td>
                </tr>
              );
            })}
            {!loading && sorted.length === 0 && !loadError && (
              <tr><td colSpan={SUMMARY_COLS.length} className="px-4 py-12 text-center text-slate-400">{search ? 'No vehicles match your search.' : 'No vehicles found.'}</td></tr>
            )}
            {loading && <tr><td colSpan={SUMMARY_COLS.length} className="px-4 py-12 text-center text-slate-400">Loading vehicles…</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards (same row-click behavior) */}
      <div className="space-y-2 sm:hidden">
        {!loading && sorted.map((v) => {
          const active = (v.status || 'active') === 'active';
          return (
            <button key={v.id} onClick={() => setProfileId(v.id)} data-testid={`assets-vehicle-card-${v.id}`} className={`w-full rounded-xl border border-slate-200 bg-white p-3 text-left ${active ? '' : 'opacity-60'}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold" style={{ color: NAVY }}>#{v.veh_number} · {v.make} {v.model}</span>
                {!active && <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">Retired</span>}
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-slate-600">
                <div><span className="text-slate-400">Driver:</span> {v.assigned_driver_name || 'Pool'}</div>
                <div><span className="text-slate-400">Tag:</span> {v.tag}</div>
                <div className="flex items-center gap-1"><span className="text-slate-400">Tag Exp:</span> {fmtD(v.registration_expiration) || '—'}
                  {expiryInfo(v.registration_expiration) && (
                    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${expiryInfo(v.registration_expiration).cls}`}><span className={`h-1.5 w-1.5 rounded-full ${expiryInfo(v.registration_expiration).dot}`} />{expiryInfo(v.registration_expiration).label}</span>
                  )}
                </div>
                <div><span className="text-slate-400">Last Service:</span> {fmtD(v.ops_latest_service_date) || '—'}</div>
                <div><span className="text-slate-400">Last Insp:</span> {fmtD(v.ops_latest_inspection_date) || '—'}</div>
              </div>
            </button>
          );
        })}
        {!loading && sorted.length === 0 && !loadError && (
          <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-400">{search ? 'No vehicles match your search.' : 'No vehicles found.'}</div>
        )}
      </div>

      {/* Governance / data-integrity note: orphaned inspection link (documented, unchanged) */}
      <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50/70 p-4" data-testid="assets-vehicles-orphan-note">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <div className="font-bold">Unresolved vehicle link — preserved, not changed</div>
            <p className="mt-1">Vehicle inspection <b>VI-20251120-984</b> references <code className="rounded bg-white px-1 py-0.5 text-xs">vehicle_id 0ddbea16…be4b</code>, which does not match any of the current vehicle master records. It has been left exactly as-is (no guessing which vehicle it belongs to).</p>
            <p className="mt-1"><b>Evidence needed to resolve:</b> the original inspection paperwork or the tech's record for 11/20/25 showing the vehicle number/tag/VIN, or confirmation of a retired/removed vehicle it belonged to. Once confirmed, the inspection can be re-linked to the correct master ID (or the record confirmed as belonging to a since-retired asset).</p>
          </div>
        </div>
      </div>

      <VehicleFormDialog open={formOpen} initial={editing} token={token} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />
      <StatusDialog open={!!statusTarget} vehicle={statusTarget} token={token} onClose={() => setStatusTarget(null)} onSaved={() => { setStatusTarget(null); load(); setProfileRefresh((n) => n + 1); }} />
      {profileId && <VehicleProfile vehicleId={profileId} canManage={canManage} drivers={drivers} meta={meta} refreshKey={profileRefresh} onRetire={(v) => setStatusTarget(v)} onClose={() => setProfileId(null)} onSaved={load} />}
    </div>
  );
};

const FutureCategory = ({ label }) => (
  <div data-testid={`assets-future-${label.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
    <Boxes size={30} className="mx-auto mb-3 text-slate-300" />
    <div className="text-lg font-bold" style={{ color: NAVY }}>{label}</div>
    <div className="mx-auto mt-1 max-w-md text-sm text-slate-500">Future asset category. This will be added to the same Assets master-data system — no separate master-data system will be created. No equipment records exist yet.</div>
    <span className="mt-3 inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-500">Future category — not yet available</span>
  </div>
);

const AdminAssets = ({ canManage = false }) => {
  const [cat, setCat] = useState('vehicles');
  return (
    <div data-testid="tfs-admin-assets-inner">
      <div className="mb-6">
        <div className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: GREEN }}>TFS Administration Hub</div>
        <h1 className="mt-1.5 text-[30px] font-extrabold leading-tight tracking-tight" style={{ color: NAVY }}>Assets</h1>
        <p className="mt-2 max-w-2xl text-base text-slate-500">Master records for TFS-owned equipment. Vehicles is the first asset category; large &amp; small equipment will be added later under this same system.</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => {
          const Icon = c.icon; const on = cat === c.key;
          return (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`assets-cat-${c.key}`}
              className={`inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-semibold transition-colors ${on ? 'text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
              style={on ? { background: NAVY, borderColor: NAVY } : {}}>
              <Icon size={16} /> {c.label}
              {c.status === 'future' && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${on ? 'bg-white/20' : 'bg-slate-200 text-slate-500'}`}>Future</span>}
            </button>
          );
        })}
      </div>

      {cat === 'vehicles' && <VehiclesMaster canManage={canManage} />}
      {cat === 'large_equipment' && <FutureCategory label="Large Equipment" />}
      {cat === 'small_equipment' && <FutureCategory label="Small Equipment" />}
    </div>
  );
};

export default AdminAssets;
